/* eslint-disable spire/export-styles */
import ProcessConfiguratorData from "@gerrieDev/Components/ProcessConfiguratorData";
import getConfiguratorData from "@gerrieDev/Handlers/GetConfiguratorData";
import {
    ConfiguratorResult,
    getConfiguratorProducts,
    sendConfiguratorProductRequestEmail,
    ValidProducts,
} from "@gerrieDev/Services/ConfiguratorService";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";

import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getPageByUrl, PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { contactFormStyles } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {}

interface OwnProps extends WidgetProps {
    fields: {};
    productsNotInSystem: [];
}

export interface NavigationListStyles {
    linkStyles: InjectableCss;
}

const navigationListStyles: NavigationListStyles = {
    linkStyles: {
        css: css``,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    website: state.context.website,
    location: getLocation(state),
});

const mapDispatchToProps = {
    getConfiguratorData: makeHandlerChainAwaitable(getConfiguratorData),
    addToCart: makeHandlerChainAwaitable(addToCart),
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

interface ConfiguratorWidgetState {
    productsNotInPlatform: string[];
    fetchedConfiguratorData: boolean;
    productsInPlatform: ValidProducts[];
    isModalOpen: boolean | null;
    successMessage: string;
    missingProductMessage: any;
    productsNotInArrayString: string;
}

// eslint-disable-next-line react/prefer-stateless-function
class ConfiguratorResultWidget extends React.Component<Props, ConfiguratorWidgetState> {
    setProductState(productsNotInPlatform: string[], productsInPlatform: ValidProducts[]) {
        this.setState({
            productsNotInPlatform,
            fetchedConfiguratorData: true,
            productsInPlatform,
        });
        if (productsNotInPlatform.length > 0) {
            let productsInArray = "";
            productsNotInPlatform.forEach(product => {
                if (productsInArray.length < 1) {
                    productsInArray = `${product}`;
                } else {
                    productsInArray = `${productsInArray}, ${product}`;
                }
            });
            productsInArray = productsInArray.trim();
            this.setState({
                isModalOpen: true,
                successMessage: translate("Configurator_ProductsNotAvailable"),
                productsNotInArrayString: productsInArray,
            });
        }
    }

    // eslint-disable-next-line class-methods-use-this
    addProductsToCart(productsInPlatform: ValidProducts[]) {
        productsInPlatform.forEach(product => {
            if (!this.state.fetchedConfiguratorData) {
                this.setState(
                    {
                        fetchedConfiguratorData: true,
                    },
                    async () => {
                        this.props.toaster.addToast({
                            body: translate("Adding available products to cart. Please wait..."),
                            messageType: "success",
                            timeoutLength: 1000 * 60,
                            onClose: () => {
                                this.props.history.push("/RockwellConfigurator");
                            },
                        });
                        const cartline = (await this.props.addToCart({
                            productId: product.productId,
                            qtyOrdered: product.qty,
                            unitOfMeasure: product.unitOfMeasure,
                            onError: () => {
                                this.props.toaster.addToast({
                                    body: translate("The product has been added to your cart."),
                                    messageType: "warning",
                                });
                            },
                        })) as any;
                        this.props.toaster.addToast({
                            body: translate(
                                "The product has been added to your cart. You will now be redirected to the configurator.",
                            ),
                            messageType: "success",
                            timeoutLength: 1000 * 60,
                            onClose: () => {
                                this.props.history.push("/RockwellConfigurator");
                            },
                        });
                    },
                );
            }
        });
    }

    modalCloseHandler() {
        this.setState({
            isModalOpen: false,
        });
    }

    setMissingProductMessage(event: any) {
        this.setState({
            missingProductMessage: event.target.value as string,
        });
    }

    continueButtonClickHandler() {
        this.setState({
            isModalOpen: false,
        });
        sendConfiguratorProductRequestEmail({
            customerMessage: this.state.missingProductMessage,
            products: this.state.productsNotInPlatform,
            requestingCustomer: "",
        });
        this.props.toaster.addToast({
            body: (
                <>
                    <Typography>
                        {translate(
                            "Product request has been sent to customer service. Thank you! Close this message to return to the configurator.",
                        )}
                    </Typography>
                </>
            ),
            messageType: "info",
            timeoutLength: 1000 * 60 * 60 * 24 * 365,
            onClose: () => {
                this.props.history.push("/RockwellConfigurator");
            },
        });
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            fetchedConfiguratorData: false,
            productsNotInPlatform: [],
            productsInPlatform: [],
            isModalOpen: null,
            successMessage: "",
            missingProductMessage: "",
            productsNotInArrayString: "",
        };
        this.modalCloseHandler = this.modalCloseHandler.bind(this);
        this.setMissingProductMessage = this.setMissingProductMessage.bind(this);
        this.continueButtonClickHandler = this.continueButtonClickHandler.bind(this);
    }

    render() {
        const parsedQuery = parseQueryString<{
            cid: string;
        }>(this.props.location.search);

        const modalStyles = {};

        if (!this.state.fetchedConfiguratorData) {
            getConfiguratorProducts(parsedQuery.cid).then(result => {
                const configuratorResult = (result as any).result as ConfiguratorResult;
                this.addProductsToCart(configuratorResult.productsCanAddToCart);
                this.setProductState(configuratorResult.productsNotInPlatform, configuratorResult.productsCanAddToCart);
            });
        }
        return (
            <div>
                <Typography>{translate("Processing configurator products...")}</Typography>
                <Modal
                    headline={translate("Products Unavailable")}
                    contentLabel={this.state.successMessage}
                    {...modalStyles}
                    isOpen={this.state.isModalOpen === true}
                    handleClose={this.modalCloseHandler}
                >
                    <GridContainer>
                        <GridItem width={12}>
                            <Typography>{this.state.productsNotInArrayString}</Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <Typography>{this.state.successMessage}</Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <TextArea
                                onChange={this.setMissingProductMessage}
                                value={this.state.missingProductMessage}
                            />
                        </GridItem>
                        <GridItem>
                            <Button onClick={this.continueButtonClickHandler}>{translate("Submit Request")}</Button>
                        </GridItem>
                    </GridContainer>
                </Modal>
            </div>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(ConfiguratorResultWidget))),
    definition: {
        group: "Basic",
        icon: "ConfiguratorResultWidget",
        fieldDefinitions: [],
    },
};

export default widgetModule;
